import React, { useState, useMemo, useEffect, useRef } from 'react';
import { UserLocation, AskRequestStatus, AskRequestStatuses } from '@hone-automation/common';
import { prefixClass } from 'lib/utils';
import { IconLoupe } from 'components/Icons';
import AvatarMini from '../AvatarMini';
import type { AssignedToType } from '../../types/Todos';
import './UserSearch.scss';
import classNames from 'classnames';

export interface UserSearchUser extends Omit<UserLocation, 'role' | 'isHoneInternalUser'> {
  isOnline?: boolean;
  name: string;
  role?: string;
  isHoneInternalUser?: boolean;
}

/** Minimal user info needed to derive avatar type */
export interface DeriveUserTypeInput {
  name?: string;
  role?: string;
  isHoneInternalUser?: boolean;
}

/**
 * Derive avatar type from user data
 */
export const deriveUserType = (user: DeriveUserTypeInput): AssignedToType => {
  const role = user.role?.toLowerCase();
  const nameLower = user.name?.toLowerCase();

  // KAI detection
  if (nameLower === 'kit' || nameLower === 'hone admin') {
    return 'kai';
  }

  if (role?.includes('external')) {
    return 'ext-email';
  }

  if (role?.includes('client')) {
    return 'client';
  }

  if (user.isHoneInternalUser) {
    return 'ks';
  }

  // Default to client if no clear indicator
  return 'client';
};

/** Special item types for quick actions */
export type SpecialItemType = 'unassigned' | 'kai' | 'assign-to-me' | 'assign-to-client' | 'assign-to-team';

export interface UserSearchProps {
  /** List of users to search through */
  users: UserLocation[] | undefined;
  /** Placeholder text for search input */
  placeholder?: string;
  /** Callback when a user is selected */
  onUserSelect?: (user: UserSearchUser) => void;
  /** Callback when a special item is selected */
  onSpecialSelect?: (type: SpecialItemType, userId?: string) => void;
  /** Callback when dropdown should close */
  onClose?: () => void;
  /** Whether the search is open/visible */
  isOpen?: boolean;
  /** Initial search text */
  searchText?: string;
  /** Optional callback to receive filtered users */
  getFilteredUsers?: (users: UserSearchUser[]) => void;
  /** Current user info for "Assign to me" option */
  currentUser?: { userId: string; name: string };
  /** Show special items (unassigned, KAI, assign to me) */
  showSpecialItems?: boolean;
  /** Whether current user can manage queue (KS user vs client) */
  canManageQueue?: boolean;
  /** Current request status - determines "Assign to Client/Team" option */
  requestStatus?: AskRequestStatus;
  /** Location name for "Assign to [Location]" label (used when canManageQueue=true) */
  locationName?: string;
  /** Whether this is an internal team only request (all comments are internal) */
  isInternalRequest?: boolean;
}

/**
 * KSQ2 UserSearch - Searchable user dropdown
 * Used for assigning users, mentions, etc.
 */
export const UserSearch: React.FC<UserSearchProps> = ({
  users,
  placeholder = 'Search users...',
  onUserSelect,
  onSpecialSelect,
  onClose,
  isOpen = true,
  searchText = '',
  getFilteredUsers,
  currentUser,
  showSpecialItems = false,
  canManageQueue = true,
  requestStatus,
  locationName,
  isInternalRequest = false,
}) => {
  const prefix = prefixClass('ksq2-user-search');

  const [searchTerm, setSearchTerm] = useState<string>(searchText);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const listRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Special items configuration
  const specialItems: { type: SpecialItemType; label: string; avatarType: AssignedToType }[] = useMemo(() => {
    if (!showSpecialItems) return [];
    const items: { type: SpecialItemType; label: string; avatarType: AssignedToType }[] = [
      { type: 'unassigned', label: 'Unassigned', avatarType: 'unassigned' },
      // KAI option hidden for now - uncomment when ready
      // { type: 'kai', label: 'KAI (coming soon)', avatarType: 'kai' },
    ];
    // For internal requests, don't show "Assign to Client" options
    if (!isInternalRequest) {
      // Assign options based on status and user type:
      // Accountant: Open → "Assign to [Location]", In Progress → "Assign to My Team"
      // Client: Open → "Assign to KS", In Progress → "Assign to My Team"
      if (requestStatus === AskRequestStatuses.Open) {
        if (canManageQueue) {
          const clientLabel = locationName ? `Assign to ${locationName}` : 'Assign to Client';
          items.push({ type: 'assign-to-client', label: clientLabel, avatarType: 'client' });
        } else {
          items.push({ type: 'assign-to-client', label: 'Assign to KS', avatarType: 'ks' });
        }
      } else if (requestStatus === AskRequestStatuses['In Progress']) {
        items.push({ type: 'assign-to-team', label: 'Assign to My Team', avatarType: 'ks' });
      }
    }
    if (currentUser) {
      items.push({ type: 'assign-to-me', label: 'Assign to me', avatarType: 'ks' });
    }
    return items;
  }, [showSpecialItems, currentUser, canManageQueue, requestStatus, locationName, isInternalRequest]);

  // Cast users to include name field, exclude current user if showing special items, sort alphabetically
  const allUsers = useMemo(() => {
    if (!users) return [];
    return (
      (users as UserSearchUser[])
        .filter(user => user.name && user.name.trim() !== '')
        .filter(user => !(showSpecialItems && currentUser && user.userId === currentUser.userId))
        // TODO: Eventually use user abilities instead of role check
        .filter(user => !isInternalRequest || user.role === 'Bookkeeper_Account_Admin')
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [users, showSpecialItems, currentUser, isInternalRequest]);

  // Filter users by search term
  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allUsers;

    const filtered = allUsers.filter(user => user.name.toLowerCase().includes(term));

    if (getFilteredUsers) {
      getFilteredUsers(filtered);
    }

    return filtered;
  }, [allUsers, searchTerm, getFilteredUsers]);

  // Filter special items by search term
  const filteredSpecialItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return specialItems;
    return specialItems.filter(item => item.label.toLowerCase().includes(term));
  }, [specialItems, searchTerm]);

  // Total items count for keyboard navigation
  const totalItems = filteredSpecialItems.length + filteredUsers.length;

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [totalItems]);

  // Sync with external searchText
  useEffect(() => {
    setSearchTerm(searchText);
  }, [searchText]);

  // Auto-focus when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current && isOpen) {
      selectedItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex, isOpen]);

  const handleUserSelect = (user: UserSearchUser) => {
    onUserSelect?.(user);
    setSelectedIndex(0);
  };

  const handleSpecialItemSelect = (item: { type: SpecialItemType; label: string }) => {
    if (item.type === 'assign-to-me' && currentUser) {
      onSpecialSelect?.(item.type, currentUser.userId);
    } else {
      onSpecialSelect?.(item.type);
    }
    setSelectedIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (totalItems === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, totalItems - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        // Check if selecting a special item or a user
        if (selectedIndex < filteredSpecialItems.length) {
          handleSpecialItemSelect(filteredSpecialItems[selectedIndex]);
        } else {
          const userIndex = selectedIndex - filteredSpecialItems.length;
          if (filteredUsers[userIndex]) {
            handleUserSelect(filteredUsers[userIndex]);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose?.();
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <div className={prefix()}>
      {/* Search input */}
      <div className={prefix('__search')}>
        <span className={prefix('__search-icon')}>
          <IconLoupe />
        </span>
        <input
          ref={inputRef}
          type="text"
          className={prefix('__input')}
          placeholder={placeholder}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>

      {/* User list */}
      <div className={prefix('__list')} ref={listRef}>
        {/* Special items */}
        {filteredSpecialItems.map((item, index) => (
          <div
            key={item.type}
            ref={index === selectedIndex ? selectedItemRef : null}
            className={classNames(prefix('__item'), prefix('__item--special'), {
              [prefix('__item--selected')]: index === selectedIndex,
            })}
            onClick={() => handleSpecialItemSelect(item)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSpecialItemSelect(item);
              }
            }}
          >
            <AvatarMini
              id={item.type}
              type={item.avatarType}
              name={item.type === 'assign-to-me' ? currentUser?.name : item.label}
            />
            <span className={prefix('__name')}>{item.label}</span>
          </div>
        ))}

        {/* Divider between special items and users */}
        {filteredSpecialItems.length > 0 && filteredUsers.length > 0 && <div className={prefix('__divider')} />}

        {/* Regular users */}
        {filteredUsers.map((user, index) => {
          const actualIndex = filteredSpecialItems.length + index;
          return (
            <div
              key={user.userId}
              ref={actualIndex === selectedIndex ? selectedItemRef : null}
              className={classNames(prefix('__item'), {
                [prefix('__item--selected')]: actualIndex === selectedIndex,
                '__item--online': user.isOnline,
              })}
              onClick={() => handleUserSelect(user)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleUserSelect(user);
                }
              }}
            >
              <AvatarMini id={user.userId} type={deriveUserType(user)} name={user.name} />
              <span className={prefix('__name')}>{user.name}</span>
            </div>
          );
        })}

        {totalItems === 0 && searchTerm && <div className={prefix('__empty')}>No users found</div>}
      </div>
    </div>
  );
};

export default UserSearch;
